import { useQuery } from "@tanstack/react-query";
import { Input } from "@rumbledirect/ui";
import { Form, Space } from "antd";
import { fetchUserRoles } from "../../../../lib/api/resources/users";
import { fetchCompanies } from "@/lib/api/resources/companies";
import { Company } from "@rumble-it/common-lib/types/models/Company.interface";
import { Button, Select } from "@rumbledirect/ui";

export interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  dealershipId?: string | null;
  roleId?: string;
}

interface UserFormProps {
  initialValues?: Partial<UserFormValues>;
  loading?: boolean;
  submitLabel?: string;
  onSubmit: (values: UserFormValues) => void;
}

export const UserForm = ({
  initialValues,
  loading,
  submitLabel = "Opslaan",
  onSubmit,
}: UserFormProps) => {
  const [form] = Form.useForm<UserFormValues>();

  const rolesQuery = useQuery({
    queryKey: ["userRoles"],
    queryFn: fetchUserRoles,
    select: (data) =>
      data.map((role) => ({
        label: role.name,
        value: role.id,
        dealershipRequired: role.dealershipRequired,
      })),
  });

  // determine if any available role requires a dealership
  const anyRoleRequiresDealership =
    rolesQuery.data && rolesQuery.data.some((r) => r.dealershipRequired);

  // helper to check if a role requires a dealership
  const roleRequiresDealership = (roleId?: string) => {
    if (!roleId) return false;
    const role = rolesQuery.data?.find((r) => r.value === roleId);
    return role && role.dealershipRequired;
  };

  // fetch dealerships only when needed: some role exists that requires it
  const dealershipsQuery = useQuery({
    queryKey: ["dealerships"],
    queryFn: () => fetchCompanies({ type: "dealership", pagination: false }),
    enabled: anyRoleRequiresDealership,
    select: (data) =>
      data.member.map((option: Company) => ({
        label: option.fullName,
        value: option.id,
      })),
  });

  const handleSubmit = (values: UserFormValues) => {
    // Clean up dealershipId if the selected role doesn't require it
    const cleanedValues = { ...values };
    if (!roleRequiresDealership(values.roleId) || !values.dealershipId) {
      cleanedValues.dealershipId = null;
    }
    onSubmit(cleanedValues);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      disabled={loading || rolesQuery.isLoading || dealershipsQuery.isLoading}
      initialValues={initialValues}
    >
      <Form.Item
        name="firstName"
        label="Voornaam"
        rules={[{ required: true, message: "Voornaam is verplicht" }]}
      >
        <Input placeholder="Vul hier de voornaam in" />
      </Form.Item>
      <Form.Item
        name="lastName"
        label="Achternaam"
        rules={[{ required: true, message: "Achternaam is verplicht" }]}
      >
        <Input placeholder="Vul hier de achternaam in" />
      </Form.Item>
      <Form.Item
        name="email"
        label="E-mailadres"
        rules={[
          {
            required: true,
            type: "email",
            message: "E-mailadres is verplicht",
          },
        ]}
      >
        <Input placeholder="Vul hier het e-mailadres in" />
      </Form.Item>

      <Form.Item
        name="roleId"
        label="Rol"
        rules={[{ required: true, message: "Rol is verplicht" }]}
      >
        <Select placeholder="Selecteer een rol" options={rolesQuery.data} />
      </Form.Item>

      <Form.Item noStyle dependencies={["roleId"]}>
        {({ getFieldValue }) => {
          const currentRoleId = getFieldValue("roleId");

          return roleRequiresDealership(currentRoleId) &&
            dealershipsQuery.data ? (
            <Form.Item name="dealershipId" label="Dealer">
              <Select
                placeholder="Selecteer een dealer"
                allowClear
                options={dealershipsQuery.data}
              />
            </Form.Item>
          ) : null;
        }}
      </Form.Item>
      <Space>
        <Button type="primary" htmlType="submit" loading={loading}>
          {submitLabel}
        </Button>
      </Space>
    </Form>
  );
};
